import { ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { changeWrapperRowZIndex } from 'utils/bom-dom-manipulation/wrapper-row-z-index';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { formatPrice } from 'utils/string-manipulation/format-price';
import { toggleQuickBet } from 'newelements/_Betslip/managers/QuickBetManager/quickBetActions';
import { GlobalIcon } from 'components/GlobalIcon';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useCurrency } from 'hooks/useCurrency';
import { getQuickBetStake } from 'store/selectors/betslip';
import { BetslipFeatureSwitcher } from '../BetslipFeatureSwitcher';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type Props = {
  quickBet: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const ROW_Z_INDEX_INCREASE_SIZE = -1;

const bottomNavigation = SpringConfigs.BOTTOM_NAVIGATION;

export const QuickbetToggle = ({
  onClick,
  quickBet
}: Props): ReactElement | null => {
  const quickBetStake = useSelector(getQuickBetStake);

  const { formatAmount, currencyId } = useCurrency();
  const { t } = useTranslation();
  const safeAreaRef = useRef(null);

  useEffect(() => {
    let wrapperElem: HTMLElement | null = null;

    if (quickBet && safeAreaRef.current) {
      wrapperElem = changeWrapperRowZIndex(safeAreaRef.current, 1);
    }

    return () => {
      if (quickBet && wrapperElem) {
        changeWrapperRowZIndex(wrapperElem, ROW_Z_INDEX_INCREASE_SIZE, true);
      }
    };
  }, [quickBet]);

  return quickBet ? (
    <div
      className={cc([
        'betslip__safe-area',
        { 'betslip__bottom-nav': bottomNavigation }
      ])}
      ref={safeAreaRef}
      onClick={onClick}
      id="betslip-area"
    >
      <div className="quickBetToggle__info">
        <div className="quickBetToggle__info-about">
          <GlobalIcon
            lib="generic"
            name="info"
            theme="default"
            size={EIconSize._16}
            className="quickBetToggle__info-icon"
          />
          <span>{t('betslip.quickBetTooltip')}</span>
        </div>
        {currencyId ? (
          <div>{formatAmount(formatPrice(quickBetStake))}</div>
        ) : (
          <Skeleton title={{ style: { width: 32 } }} paragraph={false} />
        )}
      </div>

      <BetslipFeatureSwitcher
        skipRowStyles={isMobile()}
        featureIsActive={quickBet}
        setFeatureIsActive={toggleQuickBet}
        featureName={t('betslip.quickbet')}
      />
    </div>
  ) : null;
};
